import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import { normalizePro20Response } from '@/shared/api/pro20-transform';

import type { BackofficeLoginResult } from '@/features/backoffice2020/types/backoffice-auth.types';
import { normalizeBackofficeAuthUser } from '@/features/backoffice2020/utils/normalize-backoffice-auth-user';

export interface BackofficeLoginRequest {
  email: string;
  password: string;
}

interface BackofficeLoginResults {
  access_token?: unknown;
  user?: unknown;
}

function resolveLoginResult(response: unknown): BackofficeLoginResult {
  if (typeof response === 'string' && response.trim()) {
    return {
      accessToken: response.trim(),
      user: null,
    };
  }

  const normalized = normalizePro20Response<string | BackofficeLoginResults>(
    response as never,
  );

  if (typeof normalized === 'string' && normalized.trim()) {
    return {
      accessToken: normalized.trim(),
      user: null,
    };
  }

  if (
    normalized &&
    typeof normalized === 'object' &&
    typeof normalized.access_token === 'string' &&
    normalized.access_token.trim()
  ) {
    return {
      accessToken: normalized.access_token.trim(),
      user: normalizeBackofficeAuthUser(normalized.user),
    };
  }

  throw new Error('Invalid backoffice login response');
}

export const backofficeAuthApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    backofficeLogin: builder.mutation<
      BackofficeLoginResult,
      BackofficeLoginRequest
    >({
      query: (body) => ({
        url: '/auth/backoffice/login',
        method: 'POST',
        body: {
          email: body.email,
          password: body.password,
        },
      }),
      transformResponse: (response: unknown) => resolveLoginResult(response),
      invalidatesTags: [{ type: API_TAGS.Branch, id: 'MINIMIZED' }],
    }),

    backofficeLogout: builder.mutation<null, undefined>({
      query: () => ({
        url: '/auth/backoffice/logout',
        method: 'POST',
      }),
      transformResponse: (response: unknown) => {
        if (response == null) {
          return null;
        }
        normalizePro20Response(response);
        return null;
      },
      invalidatesTags: [{ type: API_TAGS.Branch, id: 'MINIMIZED' }],
    }),

    /** Update the signed-in backoffice user's password. */
    backofficeChangePassword: builder.mutation<
      null,
      { newPassword: string; newPasswordConfirmation: string }
    >({
      query: ({ newPassword, newPasswordConfirmation }) => ({
        url: '/auth/backoffice/change-password',
        method: 'PUT',
        body: {
          newPassword,
          newPasswordConfirmation,
        },
      }),
      transformResponse: (response: unknown) => {
        if (response == null) {
          return null;
        }
        normalizePro20Response(response);
        return null;
      },
    }),
  }),
});

export const {
  useBackofficeLoginMutation,
  useBackofficeLogoutMutation,
  useBackofficeChangePasswordMutation,
} = backofficeAuthApi;
